/**
 * Account and billing screen.
 *
 * Shows the signed-in account's details and its subscription: plan, status,
 * renewal date and any payment-failure warning. Free accounts get an "Upgrade"
 * route to the pricing page; subscribed accounts get "Manage billing", which opens
 * the Stripe Customer Portal.
 *
 * The entitlement shown here is read from the account record, which is written
 * only by the server from verified Stripe webhooks (§13). Returning from Checkout
 * grants nothing on its own: on a `?checkout=success` return we simply re-read the
 * account a few times while the webhook lands, and the plan updates when it does.
 */

import { el, icon } from "../ui/dom.js";
import { errorAlert, pageHeader, section, successAlert } from "../ui/patterns.js";

/** @type {Record<import("../types/models.js").SubscriptionStatus, string>} */
const STATUS_LABEL = {
  none: "No plan",
  free: "Free",
  trialling: "Trial",
  active: "Active",
  past_due: "Payment overdue",
  cancel_at_period_end: "Ending soon",
  expired: "Expired",
  unpaid: "Unpaid",
};

/** @type {Record<string, string>} */
const PLAN_LABEL = {
  free: "Free",
  family: "Family",
  organisation: "Organisation",
};

/** Statuses that mean the account holds a Stripe subscription to manage. */
const SUBSCRIBED = new Set(["active", "trialling", "cancel_at_period_end", "past_due", "unpaid"]);

/**
 * Format an epoch-ms timestamp as a UK long date.
 *
 * @param {number} ms
 * @returns {string}
 */
function formatDate(ms) {
  return new Date(ms).toLocaleDateString("en-GB", {
    day: "numeric",
    month: "long",
    year: "numeric",
  });
}

/** @type {import("../app/router.js").PageRenderer} */
export default function renderAccount(context) {
  const account = context.services.auth.getCurrentAccount();
  const frag = document.createDocumentFragment();
  frag.append(pageHeader("Account"));

  if (!account) {
    return frag;
  }

  if (account.accountStatus === "pendingDeletion") {
    frag.append(
      errorAlert(
        "This account is scheduled for deletion. You can cancel it, or export your data, on the Your data page.",
      ),
    );
  }

  frag.append(
    section("Your details", [
      el("dl", { class: "kin-details" }, [
        el("dt", {}, ["Email"]),
        el("dd", {}, [account.email]),
        el("dt", {}, ["Email verified"]),
        el("dd", {}, [
          account.emailVerified
            ? el("wa-badge", { variant: "success" }, ["Verified"])
            : el("wa-badge", { variant: "warning" }, ["Not verified"]),
        ]),
      ]),
    ]),
  );

  frag.append(renderSubscription(context, account));

  frag.append(
    section("Data and privacy", [
      el("p", { class: "kin-note" }, [
        "See what KinCode holds about you, download it, or delete your account.",
      ]),
      el(
        "wa-button",
        {
          class: "kin-button-block",
          appearance: "outlined",
          href: "/your-data",
          dataset: { link: "" },
        },
        [icon("shield-halved"), "Your data"],
      ),
    ]),
  );

  frag.append(
    section("Session", [
      el(
        "wa-button",
        {
          class: "kin-button-block",
          variant: "danger",
          appearance: "outlined",
          on: {
            click: async () => {
              await context.services.auth.signOut();
              context.navigate("/");
            },
          },
        },
        [icon("right-to-bracket"), "Sign out"],
      ),
    ]),
  );

  return frag;
}

/**
 * Build the subscription section for an account.
 *
 * @param {import("../app/router.js").RouteContext} context
 * @param {import("../types/models.js").Account} account
 * @returns {HTMLElement}
 */
function renderSubscription(context, account) {
  const status = account.subscriptionStatus;
  const planId = account.planId ?? "free";
  const subscribed = SUBSCRIBED.has(status);

  /** @type {(Node | string)[]} */
  const children = [];

  // A newly returned Checkout does not grant anything; it just prompts a re-read.
  const returnStatus = new URLSearchParams(window.location.search).get("checkout");
  if (returnStatus === "success") {
    // Drop the query so a re-render (triggered by refresh below) does not re-run
    // this branch, and poll the account while the webhook catches up.
    window.history.replaceState(null, "", "/account");
    children.push(
      successAlert("Thank you. We are confirming your subscription — this can take a moment."),
    );
    pollForEntitlement(context);
  }

  children.push(
    el("p", {}, [
      "Plan: ",
      el("strong", {}, [PLAN_LABEL[planId] ?? "Free"]),
      status === "free" || status === "none" ? null : ` — ${STATUS_LABEL[status]}`,
    ]),
  );

  if (account.renewalAt && (status === "active" || status === "trialling")) {
    children.push(el("p", { class: "kin-note" }, [`Renews on ${formatDate(account.renewalAt)}.`]));
  } else if (account.renewalAt && status === "cancel_at_period_end") {
    children.push(
      el("p", { class: "kin-note" }, [
        `Your subscription ends on ${formatDate(account.renewalAt)}. Your profiles stay ` +
          "recoverable, and you can resubscribe at any time.",
      ]),
    );
  }

  if (status === "past_due") {
    const until =
      account.gracePeriodEndsAt !== null && account.gracePeriodEndsAt !== undefined
        ? ` You can keep managing your profiles until ${formatDate(account.gracePeriodEndsAt)}.`
        : "";
    children.push(
      errorAlert(
        `A recent payment did not go through.${until} Update your payment details to keep your ` +
          "subscription. Your saved contacts stay recoverable either way.",
      ),
    );
  }

  const errorRegion = el("div", { "aria-live": "assertive" });

  const action = subscribed
    ? el(
        "wa-button",
        {
          class: "kin-button-block",
          variant: "brand",
          appearance: "filled",
          on: { click: () => openPortal(context, errorRegion) },
        },
        [icon("credit-card"), "Manage billing"],
      )
    : el(
        "wa-button",
        {
          class: "kin-button-block",
          variant: "brand",
          appearance: "filled",
          href: "/pricing",
          dataset: { link: "" },
        },
        [icon("credit-card"), "Upgrade"],
      );

  children.push(
    el("p", { class: "kin-note" }, ["Saved contacts stay recoverable even if a payment is late."]),
    action,
    errorRegion,
  );

  return section("Subscription", children);
}

/**
 * Open the Stripe Customer Portal, surfacing any failure in place.
 *
 * @param {import("../app/router.js").RouteContext} context
 * @param {HTMLElement} errorRegion
 */
async function openPortal(context, errorRegion) {
  errorRegion.replaceChildren();
  try {
    await context.services.billing.openPortal();
    // On success the browser is redirected to Stripe.
  } catch (error) {
    errorRegion.replaceChildren(
      errorAlert(
        error instanceof Error ? error.message : "Could not open billing. Please try again.",
      ),
    );
  }
}

/**
 * Re-read the account a few times after a Checkout return, so the plan appears as
 * soon as the webhook has written it without the user needing to refresh.
 *
 * @param {import("../app/router.js").RouteContext} context
 */
function pollForEntitlement(context) {
  const delays = [1500, 4000, 8000];
  for (const delay of delays) {
    setTimeout(() => {
      // refresh() re-reads the record and notifies subscribers, which re-renders
      // this page. Safe to call even if the user has navigated away.
      void context.services.auth.refresh();
    }, delay);
  }
}
